import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, MenuItem, Select, TableRow, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar, demarcaciones, formatearFecha, getInicialesNombre, lateralidades } from '../../utils/UtilesComun';
import { obtenerCategoriaEstimada } from '../../utils/UtilesJugador';
import BotonObservacionesLista from '../botones/BotonObservacionesLista';
import { AvatarApp, ButtonTableApp, TableCellApp, TableModuleApp, TypographyApp } from '../custom/CustomComponents';

const columns = [
  { field: 'nombreJugador', label: 'Nombre', width: '27%', tooltip: 'Nombre del jugador' },
  {
    field: 'botonObservaciones',
    label: 'Nota',
    width: '8%',
    sortable: false,
    tooltip: 'Observaciones del jugador',
    sx: { textAlign: 'center' },
  },
  { field: 'nombreEquipo', label: 'Equipo', width: '20%', tooltip: 'Equipo actual' },
  { field: 'ano', label: 'Año', width: '5%', tooltip: 'Año de nacimiento', sx: { textAlign: 'center' } },
  {
    field: 'categoria',
    label: 'Cat',
    width: '5%',
    tooltip: 'Categoría estimada para la temporada',
    sx: { textAlign: 'center' },
  },
  { field: 'demarcacion', label: 'Demarcación', width: '12%', tooltip: 'Demarcación habitual' },
  { field: 'lateralidad', label: 'Lateralidad', width: '11%', tooltip: 'Pierna / Lateralidad' },
  { field: 'fecha', label: 'Fecha', width: '7%', tooltip: 'Fecha de inserción en lista' },
  { field: 'acciones', label: '', width: '5%', sortable: false, sx: { textAlign: 'center' } },
];

/**
 * Fila compacta y moderna de la tabla de jugadores en una lista.
 */
const FilaLista = ({ fila, columns, onUpdate, canEdit, onDelete, temporada }) => {
  const { modificarJugador, idUsuario } = useContext(Context);
  const navigate = useNavigate();

  const [demarcacion, setDemarcacion] = useState(fila.demarcacion || '');
  const [lateralidad, setLateralidad] = useState(fila.lateralidad || '');

  const handleDemarcacionChange = (e) => {
    const value = e.target.value;
    setDemarcacion(value);
    modificarJugador(fila.idJugador, { demarcacion: value });
  };

  const handleLateralidadChange = (e) => {
    const value = e.target.value;
    setLateralidad(value);
    modificarJugador(fila.idJugador, { lateralidad: value });
  };

  const catEstimada = fila.ano ? obtenerCategoriaEstimada(fila.ano, temporada) : null;
  const catCodigo = catEstimada?.codigo || '-';
  const catNombre = catEstimada?.nombre || 'Sin categoría';

  return (
    <TableRow
      key={fila.idJugador}
      hover
      sx={{
        height: 36,
        transition: 'background-color 0.15s ease-in-out',
        '&:hover': {
          backgroundColor: '#f1f5f9 !important',
        },
        '& td': {
          py: 0.5,
          px: 1,
        },
      }}
    >
      {columns.map((col) => {
        if (col.field === 'botonObservaciones') {
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center' }}>
              <BotonObservacionesLista
                idJugador={fila.idJugador}
                observaciones={fila.observaciones}
                badge={true}
                onUpdate={onUpdate}
              />
            </TableCellApp>
          );
        }

        if (col.field === 'nombreJugador') {
          return (
            <TableCellApp key={col.field}>
              <ButtonTableApp
                onClick={() => navigate(`/jugador/${fila.idJugador}`)}
                text={capitalizar(fila.nombreJugador)}
                tooltip={capitalizar(fila.nombreJugador)}
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  fontSize: '0.82rem',
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'transparent',
                  },
                }}
              />
            </TableCellApp>
          );
        }

        if (col.field === 'nombreEquipo') {
          const clubDisplay = fila.nombreEquipo || fila.clubActual;
          return (
            <TableCellApp key={col.field}>
              {fila.idEquipo ? (
                <ButtonTableApp
                  onClick={() => navigate(`/equipo/${fila.idEquipo}`)}
                  text={capitalizar(clubDisplay)}
                  sx={{ fontSize: '0.82rem', color: 'text.primary' }}
                />
              ) : (
                <TypographyApp sx={{ fontSize: '0.82rem', color: 'text.secondary', px: 0.5 }}>
                  {capitalizar(clubDisplay) || '-'}
                </TypographyApp>
              )}
            </TableCellApp>
          );
        }

        if (col.field === 'ano') {
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center' }}>
              <TypographyApp sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary' }}>
                {fila.ano || '-'}
              </TypographyApp>
            </TableCellApp>
          );
        }

        if (col.field === 'categoria') {
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center' }}>
              {catEstimada ? (
                <Tooltip title={catNombre} arrow placement="top">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 28,
                      height: 20,
                      px: 0.6,
                      borderRadius: '4px',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      backgroundColor: '#f1f5f9',
                      color: '#334155',
                      border: '1px solid #cbd5e1',
                      userSelect: 'none',
                    }}
                  >
                    {catCodigo}
                  </Box>
                </Tooltip>
              ) : (
                <TypographyApp sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>-</TypographyApp>
              )}
            </TableCellApp>
          );
        }

        if (col.field === 'demarcacion') {
          return (
            <TableCellApp key={col.field}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 24,
                  px: 0.75,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  maxWidth: 110,
                  width: '100%',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': canEdit
                    ? {
                        borderColor: '#0f766e',
                        backgroundColor: '#ffffff',
                      }
                    : {},
                }}
              >
                <Select
                  id={`demarcacion-select-${fila.idJugador}`}
                  value={demarcacion || ''}
                  onChange={handleDemarcacionChange}
                  disabled={!canEdit}
                  displayEmpty
                  variant="standard"
                  disableUnderline
                  renderValue={(selected) => (
                    <span
                      style={{
                        fontSize: '0.76rem',
                        fontWeight: 600,
                        color: selected ? '#1e293b' : '#94a3b8',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selected || '-'}
                    </span>
                  )}
                  sx={{
                    width: '100%',
                    '& .MuiSelect-select': {
                      py: 0,
                      pl: 0,
                      pr: '14px !important',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiSelect-icon': {
                      right: -2,
                      fontSize: '0.9rem',
                      color: '#64748b',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 220,
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        '& .MuiMenuItem-root': {
                          fontSize: '0.78rem',
                          py: 0.5,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    -
                  </MenuItem>
                  {demarcaciones.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ fontSize: '0.78rem' }}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'lateralidad') {
          return (
            <TableCellApp key={col.field}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 24,
                  px: 0.75,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '4px',
                  maxWidth: 100,
                  width: '100%',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': canEdit
                    ? {
                        borderColor: '#0f766e',
                        backgroundColor: '#ffffff',
                      }
                    : {},
                }}
              >
                <Select
                  id={`lateralidad-select-${fila.idJugador}`}
                  value={lateralidad || ''}
                  onChange={handleLateralidadChange}
                  disabled={!canEdit}
                  displayEmpty
                  variant="standard"
                  disableUnderline
                  renderValue={(selected) => (
                    <span
                      style={{
                        fontSize: '0.76rem',
                        fontWeight: 600,
                        color: selected ? '#1e293b' : '#94a3b8',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selected || '-'}
                    </span>
                  )}
                  sx={{
                    width: '100%',
                    '& .MuiSelect-select': {
                      py: 0,
                      pl: 0,
                      pr: '14px !important',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiSelect-icon': {
                      right: -2,
                      fontSize: '0.9rem',
                      color: '#64748b',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 220,
                        borderRadius: '6px',
                        border: '1px solid #e2e8f0',
                        '& .MuiMenuItem-root': {
                          fontSize: '0.78rem',
                          py: 0.5,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    -
                  </MenuItem>
                  {lateralidades.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={{ fontSize: '0.78rem' }}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'fecha') {
          return (
            <TableCellApp key={col.field}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                <TypographyApp sx={{ fontSize: '0.78rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  {formatearFecha(fila.fecha)}
                </TypographyApp>
                {fila.nombreUsuario && (
                  <Tooltip title={fila.nombreUsuario} arrow placement="top">
                    <AvatarApp
                      size={20}
                      esPropio={fila.idUsuario === idUsuario}
                      sx={{ fontSize: '0.65rem', width: 20, height: 20 }}
                    >
                      {getInicialesNombre(fila.nombreUsuario)}
                    </AvatarApp>
                  </Tooltip>
                )}
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'acciones') {
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center' }}>
              {canEdit && (
                <Tooltip title="Eliminar de la lista" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(fila.idJugador)}
                    sx={{
                      color: '#ef4444',
                      p: 0.5,
                      borderRadius: '6px',
                      '&:hover': {
                        backgroundColor: '#fee2e2',
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: '1.05rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </TableCellApp>
          );
        }

        return <TableCellApp key={col.field}>{fila[col.field]}</TableCellApp>;
      })}
    </TableRow>
  );
};

/**
 * Lista de jugadores de una lista en formato de tabla modular compacta.
 */
const JugadoresLista = ({
  lista,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalCount,
  sort,
  setSort,
  onUpdate,
  canEdit,
  onDelete,
  temporada,
}) => (
  <TableModuleApp
    columns={columns}
    sort={sort}
    setSort={setSort}
    data={lista}
    renderRow={(fila) => (
      <FilaLista
        key={fila.idJugador}
        fila={fila}
        columns={columns}
        onUpdate={onUpdate}
        canEdit={canEdit}
        onDelete={onDelete}
        temporada={temporada}
      />
    )}
    hasPagination
    page={page}
    setPage={setPage}
    rowsPerPage={rowsPerPage}
    setRowsPerPage={setRowsPerPage}
    totalCount={totalCount}
    rowsPerPageOptions={[50, 100, 150, 200]}
    emptyMessage="No hay jugadores que cumplan los filtros seleccionados."
  />
);

JugadoresLista.propTypes = {
  lista: PropTypes.array,
  page: PropTypes.number,
  setPage: PropTypes.func,
  rowsPerPage: PropTypes.number,
  setRowsPerPage: PropTypes.func,
  totalCount: PropTypes.number,
  sort: PropTypes.shape({
    field: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }),
  setSort: PropTypes.func,
  onUpdate: PropTypes.func,
  canEdit: PropTypes.bool,
  onDelete: PropTypes.func,
  temporada: PropTypes.string,
};

FilaLista.propTypes = {
  fila: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  onUpdate: PropTypes.func,
  canEdit: PropTypes.bool,
  onDelete: PropTypes.func,
  temporada: PropTypes.string,
};

export default JugadoresLista;
